/**
 * FeedbackWidget — «Send tilbakemelding»-knappen i toppen, raden i Meg og
 * arket de åpner
 * (replicated from brospace, restyled to Stell's tokens, Bokmål via i18next).
 *
 * D11 (#153): den globale knappen som svevde over hver skjerm er fjernet
 * (den dekket trykkmål, #49/#55). Brukeren kan fjerne bildet før sending.
 * Rekkefølgen er den samme som før: bildet tas FØR
 * arket åpnes, så arkets egen ramme aldri kommer med i bildet. The form
 * collects category, title and description (optionally Claude-drafted), and
 * submits the screenshot + console/network context as a GitLab issue.
 * Success/error is shown inline.
 *
 * #159 (steg 9e, Stell_Tilbakemelding): sidepanelet er nå et `BottomSheet`
 * med skissens tittel og X til høyre (arkets eget topphode — `SheetHeader`
 * har X til venstre og en handling øverst, og her står Send nederst).
 * Kategoriene er `Chip` uten emoji, og «Skriv utkast» er den lime knappen.
 * Skissens «Fra «skjerm»» er fortsatt utelatt.
 *
 * #187: tilbakemeldingen er tilbake på skjermen der problemet er. `variant`
 * «icon» er en rund knapp i toppen ved siden av avataren på skjermene i
 * skallet, så bildet igjen viser den skjermen; «row» er raden i Meg som før.
 * Begge skjules når familien ikke kan sende (`useFeedbackStatus`: bryteren
 * av eller GitLab ikke satt opp), i stedet for at innsendingen feiler.
 *
 * Renders nothing until a member is authenticated (gated on useSession).
 */
import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../../components/primitives/BottomSheet';
import { Button } from '../../components/primitives/Button';
import { Chip } from '../../components/primitives/Chip';
import { Icon } from '../../components/primitives/Icon';
import { ADMIN_ROW } from '../admin/adminStyles';
import { useSession } from '../auth/useSession';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { PANEL_ROUND } from '../../components/primitives/DatePanel';
import { useFeedback, type FeedbackCategory } from './useFeedback';
import { useFeedbackStatus } from './useFeedbackStatus';

const CATEGORIES: FeedbackCategory[] = ['bug', 'feature', 'question'];

// Skissens «Skriv utkast»: lime i begge temaer (fab-tokenet, samme som
// assistentknappen). Høyden er 44 px, ikke skissens 34, så målet holder ≥44.
const DRAFT_BUTTON =
  'inline-flex h-11 shrink-0 items-center gap-1.5 rounded-pill px-3.5 text-caption font-extrabold disabled:cursor-not-allowed disabled:opacity-50';
const DRAFT_LIME = `${DRAFT_BUTTON} bg-fab text-on-fab`;
const DRAFT_QUIET = `${DRAFT_BUTTON} border border-secondary-line bg-secondary text-ink`;

// Decorative glyph held as an identifier so it renders via a JSX expression
// container — react/jsx-no-literals forbids bare string literals in JSX.
const GLYPH_RELOAD = '↻';

// Knappen i toppen (#187): samme 44 px runde form som tilbakeknappen i
// ScreenHeader, eller panelets runde knapper når den står på datopanelet.
const ICON_BUTTON = {
  glass: `${GLASS_SURFACE} flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink`,
  panel: `${PANEL_ROUND} border border-on-panel/20 text-on-panel hover:bg-on-panel/10`,
} as const;

interface FeedbackWidgetProps {
  /** «row» er raden i Meg; «icon» er knappen i toppen på de andre skjermene. */
  variant?: 'row' | 'icon';
  /** Flaten knappen står på — bare for `variant="icon"`. */
  tone?: keyof typeof ICON_BUTTON;
  /**
   * Egen inngang i stedet for raden eller knappen — assistentens ⋯-meny
   * (#187), der toppen ikke har plass til én knapp til på telefonen. Får
   * `open`, eller `null` når tilbakemelding ikke er tilgjengelig; arket står
   * her, utenfor menyen, så det overlever at menyen lukkes.
   */
  trigger?: (open: (() => void) | null) => ReactNode;
}

export function FeedbackWidget({ variant = 'row', tone = 'glass', trigger }: FeedbackWidgetProps = {}) {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const enabled = useFeedbackStatus();
  const [isOpen, setIsOpen] = useState(false);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  const {
    state,
    setCategory,
    setTitle,
    setDescription,
    setScreenshotDataUrl,
    captureScreenshot,
    submit,
    reset,
    generate,
    cancelGenerate,
    checkAiAvailability,
    clearAiError,
  } = useFeedback();

  const close = useCallback(() => {
    setShowReplaceConfirm(false);
    setIsOpen(false);
    reset();
  }, [reset]);

  const handleOpenClick = useCallback(async () => {
    // Capture BEFORE the sheet is in the DOM so its chrome isn't in the shot.
    const dataUrl = await captureScreenshot();
    setScreenshotDataUrl(dataUrl);
    setIsOpen(true);
    checkAiAvailability();
  }, [captureScreenshot, setScreenshotDataUrl, checkAiAvailability]);

  // «Send en til»: arket lukkes før det nye bildet tas, så heller ikke det
  // andre bildet viser arket (D11).
  const handleNew = useCallback(() => {
    flushSync(close);
    void handleOpenClick();
  }, [close, handleOpenClick]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      await submit();
    },
    [submit],
  );

  const canGenerate = state.title.trim().length >= 3 && !!state.screenshotDataUrl;
  const hasEditedAiDraft = state.aiDraft !== null && state.description !== state.aiDraft;

  const generateTooltip = (): string | undefined => {
    if (state.aiAvailable !== true) return undefined;
    const hasTitle = state.title.trim().length >= 3;
    const hasShot = !!state.screenshotDataUrl;
    if (!hasTitle && !hasShot) return t('feedback.generate_need_both');
    if (!hasTitle) return t('feedback.generate_need_title');
    if (!hasShot) return t('feedback.generate_need_screenshot');
    return undefined;
  };

  const handleGenerateClick = () => {
    if (state.description.trim().length > 0) {
      setShowReplaceConfirm(true);
      return;
    }
    generate();
  };
  const handleRegenerateClick = () => {
    if (hasEditedAiDraft) {
      setShowReplaceConfirm(true);
      return;
    }
    generate();
  };
  const handleConfirmedGenerate = () => {
    setShowReplaceConfirm(false);
    generate();
  };

  // Don't render anything until the family member is authenticated, and not
  // when the family can't send anyway (#187).
  if (!session?.member || !enabled) return trigger ? trigger(null) : null;

  let draftButton: ReactNode = null;
  if (state.aiAvailable === true && !showReplaceConfirm) {
    if (state.isGenerating) {
      draftButton = (
        <button type="button" onClick={cancelGenerate} className={DRAFT_QUIET}>
          {t('feedback.cancel')}
        </button>
      );
    } else if (state.aiError) {
      draftButton = (
        <button type="button" onClick={() => generate()} className={DRAFT_LIME}>
          {GLYPH_RELOAD} {t('feedback.retry')}
        </button>
      );
    } else if (state.aiDraft !== null) {
      draftButton = (
        <button type="button" onClick={handleRegenerateClick} className={DRAFT_LIME}>
          {GLYPH_RELOAD} {t('feedback.regenerate')}
        </button>
      );
    } else {
      draftButton = (
        <button
          type="button"
          onClick={handleGenerateClick}
          disabled={!canGenerate}
          title={generateTooltip()}
          className={DRAFT_LIME}
        >
          <Icon.Sparkle size={14} />
          {t('feedback.generate')}
        </button>
      );
    }
  }

  return (
    <>
      {trigger ? (
        // Ett slag senere, så menyen rundt rekker å lukke seg og ikke kommer
        // med i bildet.
        trigger(() => window.setTimeout(() => void handleOpenClick(), 0))
      ) : variant === 'icon' ? (
        // #187: i toppen, så bildet viser skjermen brukeren står på.
        <button
          type="button"
          onClick={() => void handleOpenClick()}
          data-testid="feedback-header-button"
          aria-label={t('feedback.fab_label')}
          title={t('feedback.fab_label')}
          className={ICON_BUTTON[tone]}
        >
          <Icon.Feedback size={20} />
        </button>
      ) : (
        // Raden står i Meg ved siden av de andre lenkeradene. data-testid er
        // beholdt fra den gamle knappen (PLAN.md: flytt id-en til det
        // tilsvarende elementet). #159 (steg 9d): samme rad som de andre i
        // kortet; Meg legger kortet rundt.
        <button
          type="button"
          onClick={() => void handleOpenClick()}
          data-testid="feedback-fab"
          className={ADMIN_ROW}
        >
          <span className="min-w-0 flex-1">{t('feedback.fab_label')}</span>
          <Icon.ChevR size={18} className="shrink-0 text-ink-2" />
        </button>
      )}
      <BottomSheet open={isOpen} onClose={close} title={t('feedback.title')} snapPoints="full">
        {state.submitState === 'success' ? (
          <SuccessPanel issueUrl={state.issueUrl} onNew={handleNew} onClose={close} />
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
            {/* Skjermbildet */}
            {state.screenshotDataUrl ? (
              <div className="flex items-center gap-3.5">
                <img
                  src={state.screenshotDataUrl}
                  alt={t('feedback.screenshot')}
                  className="h-30 w-16 shrink-0 rounded-sm border-2 border-secondary-line object-cover object-top"
                />
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-body-s font-extrabold text-ink">
                    {t('feedback.screenshot_attached')}
                  </p>
                  <p className="m-0 text-caption leading-snug text-ink-2">
                    {t('feedback.screenshot_private')}
                  </p>
                  <button
                    type="button"
                    onClick={() => setScreenshotDataUrl(null)}
                    className="mt-0.5 min-h-11 text-control font-extrabold text-ink underline"
                  >
                    {t('feedback.screenshot_remove')}
                  </button>
                </div>
              </div>
            ) : (
              <p className="m-0 text-caption text-ink-2">{t('feedback.screenshot_unavailable')}</p>
            )}

            {/* Kategori */}
            <div role="group" aria-label={t('feedback.category_label')} className="flex gap-2">
              {CATEGORIES.map((value) => (
                <Chip
                  key={value}
                  selected={state.category === value}
                  onClick={() => setCategory(value)}
                  className="flex-1 justify-center"
                >
                  {t(`feedback.category.${value}`)}
                </Chip>
              ))}
            </div>

            {/* Tittel */}
            <div className="flex flex-col">
              <SectionLabel as="label" htmlFor="feedback-title">
                {t('feedback.field_title')}
              </SectionLabel>
              <input
                id="feedback-title"
                type="text"
                value={state.title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder={t('feedback.title_placeholder')}
                className="field"
              />
            </div>

            {/* Beskrivelse + AI-utkast */}
            <div className="flex flex-col">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <SectionLabel as="label" htmlFor="feedback-description" flush>
                  {t('feedback.description')}
                </SectionLabel>
                {draftButton}
              </div>

              {showReplaceConfirm && (
                <div className="mb-2 flex flex-wrap items-center gap-x-3 rounded-md bg-inset px-3 text-body-s text-ink">
                  <span className="py-2">{t('feedback.replace_confirm')}</span>
                  <button
                    type="button"
                    onClick={handleConfirmedGenerate}
                    className="min-h-11 font-extrabold underline"
                  >
                    {t('feedback.yes')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowReplaceConfirm(false)}
                    className="min-h-11 underline"
                  >
                    {t('feedback.cancel')}
                  </button>
                </div>
              )}

              {state.isGenerating ? (
                <div className="field flex h-27.5 animate-pulse flex-col gap-2 py-3.5">
                  <div className="h-3 w-full rounded-xs bg-track" />
                  <div className="h-3 w-5/6 rounded-xs bg-track" />
                  <div className="h-3 w-4/6 rounded-xs bg-track" />
                </div>
              ) : (
                <textarea
                  id="feedback-description"
                  value={state.description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (state.aiError) clearAiError();
                  }}
                  rows={4}
                  required
                  placeholder={t('feedback.description_placeholder')}
                  className="field resize-none leading-snug"
                />
              )}

              {state.aiDraft !== null && !state.isGenerating && !state.aiError && (
                <p className="m-0 mt-1.5 text-caption text-ink-2">{t('feedback.ai_badge')}</p>
              )}
              {state.aiError && (
                <p className="m-0 mt-1.5 text-caption text-bad">
                  {t(state.aiError === 'ai_unavailable' ? 'feedback.ai_unavailable' : 'feedback.ai_error')}
                </p>
              )}
            </div>

            {state.submitState === 'error' && (
              <p className="m-0 text-body-s text-bad">{t('feedback.error_body')}</p>
            )}

            <Button
              type="submit"
              variant="primary"
              size={56}
              disabled={state.submitState === 'submitting'}
              className="mt-1 w-full"
            >
              {state.submitState === 'submitting' ? t('feedback.sending') : t('feedback.send')}
            </Button>
            <p className="m-0 text-center text-caption text-ink-2">{t('feedback.send_hint')}</p>
          </form>
        )}
      </BottomSheet>
    </>
  );
}

function SuccessPanel({
  issueUrl,
  onNew,
  onClose,
}: {
  issueUrl: string | null;
  onNew: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-3 py-6 text-center">
      <span
        aria-hidden
        className="flex h-16 w-16 items-center justify-center rounded-full bg-lime-bg text-lime"
      >
        <Icon.Check size={32} />
      </span>
      <h3 className="m-0 text-heading-s font-extrabold text-ink">{t('feedback.success_title')}</h3>
      <p className="m-0 text-body-s text-ink-2">{t('feedback.success_body')}</p>
      {issueUrl && (
        <a
          href={issueUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center text-body-s font-extrabold text-accent-text underline"
        >
          {t('feedback.view_issue')}
        </a>
      )}
      <div className="mt-2 flex w-full flex-col gap-2">
        <Button type="button" variant="secondary" size={52} onClick={onNew} className="w-full">
          {t('feedback.new')}
        </Button>
        <Button type="button" variant="ghost" size={52} onClick={onClose} className="w-full">
          {t('feedback.close')}
        </Button>
      </div>
    </div>
  );
}
